import {
  Autocomplete,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import CustomCheckbox from 'src/components/forms/theme-elements/CustomCheckbox';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import CustomSelect from 'src/components/forms/theme-elements/CustomSelect';
import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';
import { useTournamentFormValidationSchema } from './tournamentFormValidationSchema';
import { FormikProps } from 'formik';
import { TournamentFormValues } from '../../types';
import CustomOutlinedInput from 'src/components/forms/theme-elements/CustomOutlinedInput';
import useLang from 'src/hooks/useLang';
import { paymentMethodsArr, TournamentsTypes } from 'src/services/tournaments/addTournament';
import { useSelector } from 'src/store/Store';
import { TournamentCategory } from 'src/services/tournaments/getTournamentCategories';
import ImageUploader from 'src/components/shared/FileUpload';
import useTournament from '../../hooks/useTournament';
import { useResetImageMutation } from 'src/services/tournaments';
import { useParams } from 'react-router';
import { useAlertDialog } from 'src/hooks/useAlertDialog';
import { Tournament } from 'src/services/tournaments/getTournaments';

const BasicDetails = ({
  formik,
  selectedTournament,
}: {
  formik: FormikProps<TournamentFormValues>;
  selectedTournament: Tournament;
}) => {
  const stadium = useSelector((state) => state.configReducer.selectedStadium);
  const options = useSelector((state) => state.tournamentReducer.options);

  const openAlertDialog = useAlertDialog();

  const { id } = useParams();

  const { genders } = useTournamentFormValidationSchema();
  const { getTranslatedTitle } = useLang();

  const { loadOptions } = useTournament();

  const [resetImage] = useResetImageMutation();

  const handleResetImage = (callback: () => void) => {
    if (formik.values.image && typeof formik.values.image === 'string') {
      openAlertDialog({
        title: t('TournamentsScreen.deleteImage'),
        message: t('TournamentsScreen.deleteImageMessage'),
        contentSx: { padding: 1 },
        buttons: [
          {
            text: t('TournamentsScreen.delete'),
            color: 'primary',
            onClick: () => {
              resetImage(id || selectedTournament.id);
              callback();
            },
          },
          {
            text: t('Common.cancel'),
            color: 'error',
            onClick: () => {
              return;
            },
          },
        ],
      });
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const isAnyCatrgoryOfTypeRoundRobinSingleElimination = formik.values.categories?.some(
    (c) => c.type === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION,
  );

  return (
    <>
      <Grid item xs={12} mt={3}>
        <Typography variant="h5" mb={1}>
          {t('TournamentsScreen.tournamentImage')}
        </Typography>
        <Typography color="textSecondary" mb={3}>
          {t('AddCoachPage.uploadYourImageHere')}
        </Typography>
        <ImageUploader
          name="image"
          setFieldValue={formik.setFieldValue}
          error={formik.errors.image}
          isTouched={formik.touched.image}
          value={formik.values.image}
          onReset={handleResetImage}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="name">{t('TournamentsScreen.name')}</CustomFormLabel>
        <CustomTextField
          id="name"
          fullWidth
          {...formik.getFieldProps('name')}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="name_ar">{t('TournamentsScreen.name_ar')}</CustomFormLabel>
        <CustomTextField
          id="name_ar"
          fullWidth
          {...formik.getFieldProps('name_ar')}
          error={formik.touched.name_ar && Boolean(formik.errors.name_ar)}
          helperText={formik.touched.name_ar && formik.errors.name_ar}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="sport_type_id">{t('AddCoachPage.sportType')}</CustomFormLabel>
        <CustomSelect
          id="sport_type_id"
          fullWidth
          {...formik.getFieldProps('sport_type_id')}
          error={formik.touched.sport_type_id && Boolean(formik.errors.sport_type_id)}
          helperText={formik.touched.sport_type_id && formik.errors.sport_type_id}
        >
          {options?.data?.sport_types.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {getTranslatedTitle(option, 'name')}
            </MenuItem>
          ))}
        </CustomSelect>
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="gender">{t('TournamentsScreen.gender')}</CustomFormLabel>
        <CustomSelect
          id="gender"
          fullWidth
          {...formik.getFieldProps('gender')}
          error={formik.touched.gender && Boolean(formik.errors.gender)}
          helperText={formik.touched.gender && formik.errors.gender}
        >
          {genders?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.title}
            </MenuItem>
          ))}
        </CustomSelect>
        {formik.touched.gender && formik.errors.gender && (
          <FormHelperText error>{formik.errors.gender}</FormHelperText>
        )}
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="start_date">{t('TournamentsScreen.startDate')}</CustomFormLabel>
        <CustomTextField
          id="start_date"
          fullWidth
          type="date"
          {...formik.getFieldProps('start_date')}
          error={formik.touched.start_date && Boolean(formik.errors.start_date)}
          helperText={formik.touched.start_date && formik.errors.start_date}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="end_date">{t('TournamentsScreen.endDate')}</CustomFormLabel>
        <CustomTextField
          id="end_date"
          fullWidth
          type="date"
          {...formik.getFieldProps('end_date')}
          error={formik.touched.end_date && Boolean(formik.errors.end_date)}
          helperText={formik.touched.end_date && formik.errors.end_date}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="registration_deadline">
          {t('TournamentsScreen.registrationDeadline')}
        </CustomFormLabel>
        <CustomTextField
          id="registration_deadline"
          fullWidth
          type="date"
          {...formik.getFieldProps('registration_deadline')}
          error={
            formik.touched.registration_deadline && Boolean(formik.errors.registration_deadline)
          }
          helperText={formik.touched.registration_deadline && formik.errors.registration_deadline}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="price_per_team">
          {t('TournamentsScreen.registrationPrice')}
        </CustomFormLabel>
        <CustomOutlinedInput
          id="price_per_team"
          type="number"
          fullWidth
          {...formik.getFieldProps('price_per_team')}
          startAdornment={
            <InputAdornment position="start">
              {getTranslatedTitle(stadium, 'main_currency_label')}
            </InputAdornment>
          }
        />
        {formik.touched.price_per_team && formik.errors.price_per_team && (
          <FormHelperText error>{formik.errors.price_per_team}</FormHelperText>
        )}
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="rank_type_id">
          {t('TournamentsScreen.rankingType')}
        </CustomFormLabel>
        <CustomSelect
          id="rank_type_id"
          fullWidth
          {...formik.getFieldProps('rank_type_id')}
          error={formik.touched.rank_type_id && Boolean(formik.errors.rank_type_id)}
          helperText={formik.touched.rank_type_id && formik.errors.rank_type_id}
        >
          {options?.data?.rank_types?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {getTranslatedTitle(option, 'name')}
            </MenuItem>
          ))}
        </CustomSelect>
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="rank_id">{t('TournamentsScreen.ranking')}</CustomFormLabel>
        <CustomSelect
          id="rank_id"
          fullWidth
          {...formik.getFieldProps('rank_id')}
          error={formik.touched.rank_id && Boolean(formik.errors.rank_id)}
          helperText={formik.touched.rank_id && formik.errors.rank_id}
        >
          {options?.data?.ranks?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {getTranslatedTitle(option, 'name')}
            </MenuItem>
          ))}
        </CustomSelect>
        {formik.touched.rank_id && formik.errors.rank_id && (
          <FormHelperText error>{formik.errors.rank_id}</FormHelperText>
        )}
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="tournament_type_id">
          {t('TournamentsScreen.tournamentType')}
        </CustomFormLabel>
        <CustomSelect
          id="tournament_type_id"
          fullWidth
          {...formik.getFieldProps('tournament_type_id')}
          error={formik.touched.tournament_type_id && Boolean(formik.errors.tournament_type_id)}
          helperText={formik.touched.tournament_type_id && formik.errors.tournament_type_id}
        >
          {options?.data?.tournament_types?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {getTranslatedTitle(option, 'name')}
            </MenuItem>
          ))}
        </CustomSelect>
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="interface_type">
          {t('TournamentsScreen.interfaceType')}
        </CustomFormLabel>
        <CustomSelect
          id="interface_type"
          fullWidth
          {...formik.getFieldProps('interface_type')}
          error={formik.touched.interface_type && Boolean(formik.errors.interface_type)}
          helperText={formik.touched.interface_type && formik.errors.interface_type}
        >
          {Object.entries(options?.data?.interface_types || {})?.map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </CustomSelect>
      </Grid>
      {formik.values?.tournament_type_id &&
      (formik.values?.tournament_type_id === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION ||
        isAnyCatrgoryOfTypeRoundRobinSingleElimination) ? (
        <Grid item xs={6}>
          <CustomFormLabel htmlFor="teams_per_group">
            {t('TournamentsScreen.teamsPerGroup')}
          </CustomFormLabel>
          <CustomTextField
            id="teams_per_group"
            type="number"
            {...formik.getFieldProps('teams_per_group')}
            error={formik.touched.teams_per_group && Boolean(formik.errors.teams_per_group)}
            fullWidth
            helperText={formik.touched.teams_per_group && formik.errors.teams_per_group}
          />
        </Grid>
      ) : null}
      {formik.values?.tournament_type_id &&
      (formik.values?.tournament_type_id === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION ||
        isAnyCatrgoryOfTypeRoundRobinSingleElimination) ? (
        <Grid item xs={6}>
          <CustomFormLabel htmlFor="teams_to_qualify_per_group">
            {t('TournamentsScreen.teamsToQualifyPerGroup')}
          </CustomFormLabel>
          <CustomTextField
            id="teams_to_qualify_per_group"
            type="number"
            {...formik.getFieldProps('teams_to_qualify_per_group')}
            error={
              formik.touched.teams_to_qualify_per_group &&
              Boolean(formik.errors.teams_to_qualify_per_group)
            }
            helperText={
              formik.touched.teams_to_qualify_per_group && formik.errors.teams_to_qualify_per_group
            }
            fullWidth
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <CustomFormLabel htmlFor="supported_payment_methods">
          {t('TournamentsScreen.supportedPaymentMethods')}
        </CustomFormLabel>
        <Autocomplete
          multiple
          id="supported_payment_methods"
          value={formik.values.supported_payment_methods}
          options={paymentMethodsArr}
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) => option.value == value.value}
          onChange={(_, newValue) => {
            formik.setFieldValue('supported_payment_methods', newValue);
          }}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <CustomCheckbox style={{ marginRight: 8 }} checked={selected} />
              {option.label}
            </li>
          )}
          fullWidth
          renderInput={(params) => (
            <CustomTextField
              {...params}
              placeholder={t('TournamentsScreen.selectPaymentMethods')}
              error={
                formik.touched.supported_payment_methods &&
                Boolean(formik.errors.supported_payment_methods)
              }
              helperText={
                formik.touched.supported_payment_methods && formik.errors.supported_payment_methods
              }
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomFormLabel htmlFor="categories">
          {t('TournamentsScreen.selectCategories')}
        </CustomFormLabel>
        <Autocomplete
          multiple
          id="categories"
          value={(formik.values.categories || []).map((c) => ({
            ...c,
            max_number_of_teams: c.max_number_of_teams ?? '',
            show_schedule: true as const,
          }))}
          options={
            Array.isArray(options?.data?.tournament_categories)
              ? options?.data?.tournament_categories?.map((c) => ({
                  ...c,
                  max_number_of_teams: c?.max_number_of_teams || '',
                  show_schedule: c?.show_schedule || true,
                }))
              : []
          }
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => {
            formik.setFieldValue('categories', newValue);
          }}
          getOptionLabel={(option) => getTranslatedTitle(option, 'name')}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <CustomCheckbox style={{ marginRight: 8 }} checked={selected} />
              {getTranslatedTitle(option, 'name')}
            </li>
          )}
          fullWidth
          renderInput={(params) => (
            <CustomTextField
              {...params}
              placeholder={t('TournamentsScreen.selectCategories')}
              error={formik.touched.categories && Boolean(formik.errors.categories)}
              helperText={formik.touched.categories && formik.errors.categories}
            />
          )}
        />
      </Grid>
      {formik.values.categories?.map((c, i) => (
        <CategoryInput key={i} category={c} index={i} formik={formik} />
      ))}
    </>
  );
};

export default BasicDetails;

const CategoryInput = ({
  category,
  index,
  formik,
}: {
  category: TournamentCategory;
  index: number;
  formik: FormikProps<TournamentFormValues>;
}) => {
  const [touched, setTouched] = useState(false);
  const { getTranslatedTitle } = useLang();
  const options = useSelector((state) => state.tournamentReducer.options);
  const tournamentTypeId = formik.values.tournament_type_id;

  const isCustomTournament = tournamentTypeId === TournamentsTypes.CUSTOM;

  useEffect(() => {
    if (formik.submitCount > 0) {
      setTouched(true);
    }
  }, [formik.submitCount]);

  return (
    <Grid item xs={3} key={index}>
      <CustomFormLabel sx={{ height: 50 }}>{`${getTranslatedTitle(category, 'name')} ${t(
        'TournamentsScreen.teamsLimit',
      )}`}</CustomFormLabel>
      <CustomTextField
        type="number"
        value={formik.values.categories[index].max_number_of_teams}
        error={!formik.values.categories[index].max_number_of_teams && touched}
        helperText={
          !formik.values.categories[index].max_number_of_teams && touched
            ? t('TournamentsScreen.enterTeamsLimit')
            : ''
        }
        onBlur={() => {
          setTouched(true);
        }}
        onChange={(e) => {
          const newCategories = formik.values.categories.slice();
          newCategories[index].max_number_of_teams = e.target.value;
          formik.setFieldValue('categories', newCategories);
        }}
      />
      {isCustomTournament && (
        <>
          <CustomFormLabel>{t('TournamentsScreen.categoryFormat')}</CustomFormLabel>
          <CustomSelect
            value={formik.values.categories[index].type ?? ''}
            onChange={(e) => {
              const newCategories = formik.values.categories.slice();
              newCategories[index].type = e.target.value === '' ? null : Number(e.target.value);
              formik.setFieldValue('categories', newCategories);
            }}
            fullWidth
            error={touched && !formik.values.categories[index].type}
            helperText={
              touched &&
              !formik.values.categories[index].type &&
              t('TournamentsScreen.selectCategoryFormat')
            }
          >
            {options?.data?.tournament_types?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {getTranslatedTitle(option, 'name')}
              </MenuItem>
            ))}
          </CustomSelect>
          {touched && !formik.values.categories[index].type && (
            <FormHelperText error>{t('TournamentsScreen.selectCategoryFormat')}</FormHelperText>
          )}
        </>
      )}
      <Stack alignItems="center" justifyContent="center" direction="row" spacing={1} m={0}>
        <Switch
          id="show_schedule"
          checked={category.show_schedule}
          onChange={(e) => {
            const newCategories = formik.values.categories.slice();
            newCategories[index].show_schedule = e.target.checked;
            formik.setFieldValue('categories', newCategories);
          }}
        />
        <CustomFormLabel variant="body2" htmlFor="show_schedule">
          {t('TournamentsScreen.showSchedule')}
        </CustomFormLabel>
      </Stack>
    </Grid>
  );
};
